import type { QueryStatus, UseQueryOptions } from '@tanstack/react-query';
import { createContext, type PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { resetAuthorizationToken, setAuthorizationToken } from '@front/authentication/src/token/token';

import ApiError from '@front/common/src/api/api-error';
import { useCreateSession } from '@front/common/src/queries/session-queries';
import {
  type CreateUserResult,
  useCreateUser,
  useGetUser,
  useOauthConnectUser,
} from '@front/common/src/queries/user-queries';
import type { OAuthLoginData, User } from '@front/common/src/types/commons';

export type AuthenticationStatus = 'loading' | 'authenticated' | 'unauthenticated';

const statusMapper: Record<QueryStatus, AuthenticationStatus> = {
  loading: 'loading',
  error: 'unauthenticated',
  success: 'authenticated',
};

type AuthenticationContextValue = {
  user?: User;
  status: AuthenticationStatus;
};

const AuthenticationContext = createContext<AuthenticationContextValue>({
  status: 'loading',
});

const onSuccessSetToken =
  (onSuccess?: (data: User) => void) =>
  ({ user, token }: CreateUserResult) => {
    setAuthorizationToken(token);
    if (onSuccess) onSuccess(user);
  };

const onErrorDiscardToken = resetAuthorizationToken;

type AuthenticationContextProviderProps = PropsWithChildren & {
  onAuthenticated: (user: User) => void;
};

export const AuthenticationContextProvider = ({ children, onAuthenticated }: AuthenticationContextProviderProps) => {
  const { data: user, error, status: queryStatus } = useGetUser();

  // biome-ignore lint/correctness/useExhaustiveDependencies: onAuthenticated is an effect event.
  useEffect(() => {
    if (user) onAuthenticated(user);
  }, [user]);

  useEffect(() => {
    if (error) onErrorDiscardToken();
  }, [error]);

  const value = useMemo(() => ({ status: statusMapper[queryStatus] }), [queryStatus]);
  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

export const useAuthenticationStatus = () => {
  const { status } = useContext(AuthenticationContext);
  return status;
};

export const useIsSignedIn = () => {
  const { status } = useContext(AuthenticationContext);
  return status === 'authenticated';
};

export const useIsUserReady = () => {
  const { status } = useContext(AuthenticationContext);
  return status !== 'loading';
};

export const useGetCurrentUser = () => {
  const { status } = useContext(AuthenticationContext);
  const { data } = useGetUser({ enabled: status === 'authenticated' });
  return data;
};

export const useGetSignedInUser = () => {
  const { data } = useGetUser();
  if (!data) throw new Error('User should be defined here');
  return data;
};

export const useSignIn = ({ onSuccess }: Pick<UseQueryOptions<User>, 'onSuccess'>) => {
  const {
    isLoading: isFormSignInLoading,
    error: formSignInError,
    mutate: formSignIn,
  } = useCreateSession({ onSuccess: onSuccessSetToken(onSuccess), onError: onErrorDiscardToken });
  return { isFormSignInLoading, formSignInError, formSignIn };
};

export const useSignup = ({ onSuccess }: Pick<UseQueryOptions<User>, 'onSuccess'>) => {
  const {
    isLoading: isSignupLoading,
    error: signupError,
    mutate: signup,
  } = useCreateUser({ onSuccess: onSuccessSetToken(onSuccess), onError: onErrorDiscardToken });
  return { isSignupLoading, signupError, signup };
};

type UseAuthConnectParams = Pick<UseQueryOptions<User>, 'onSuccess'> & {
  onPartialConnect: (omniAuthData: OAuthLoginData, partialUser: Partial<User>) => void;
};

export const useOauthConnect = ({ onSuccess, onPartialConnect }: UseAuthConnectParams) => {
  const {
    isLoading: isOauthConnectLoading,
    error: oauthConnectError,
    mutate: oauthConnect,
  } = useOauthConnectUser({
    onSuccess: onSuccessSetToken(onSuccess),
    onError: (error, { omniAuthData }) => {
      resetAuthorizationToken();
      if (error instanceof ApiError && error.status === 422) {
        const responseUser: Partial<User> = error.response;

        // Apple's API sends the first and last name only during the first signup attempt.
        // As Apple demands us to pre-fill the first and last name during Apple signup, we need to set them here.
        // We prioritize using the names sent by the back and otherwise set the names returned by Apple's API.
        const partialUser: Partial<User> =
          omniAuthData.provider === 'apple'
            ? {
                ...responseUser,
                first_name: responseUser.first_name || omniAuthData.firstName,
                last_name: responseUser.last_name || omniAuthData.lastName,
              }
            : responseUser;
        if (onPartialConnect) onPartialConnect(omniAuthData, partialUser);
      }
    },
  });
  return { isOauthConnectLoading, oauthConnectError, oauthConnect };
};

export const useOauthSignup = ({ onSuccess }: Pick<UseQueryOptions<User>, 'onSuccess'>) => {
  const {
    isLoading: isOauthSignupLoading,
    error: oauthSignupError,
    mutate: oauthSignup,
  } = useOauthConnectUser({ onSuccess: onSuccessSetToken(onSuccess), onError: onErrorDiscardToken });
  return { isOauthSignupLoading, oauthSignupError, oauthSignup };
};
